import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Eye, ChevronDown } from 'lucide-react';
import { adminAPI } from '../../api/admin';
import { ordersAPI } from '../../api/orders';
import toast from 'react-hot-toast';

const OrdersAdminPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const autoPrintTriggered = useRef(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [orderItems, setOrderItems] = useState<any[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('USPS');
  const [isSavingTracking, setIsSavingTracking] = useState(false);
  const [printData, setPrintData] = useState<{ order: any; items: any[] } | null>(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  // Supports the "Open & Print Order Details" link sent in the admin
  // payment-confirmed email — auto-opens and prints the matching order
  // once the orders list has loaded.
  useEffect(() => {
    const printId = searchParams.get('print');
    if (printId && orders.length > 0 && !autoPrintTriggered.current) {
      const order = orders.find((o) => o.id === printId);
      if (order) {
        autoPrintTriggered.current = true;
        handleViewOrderAndPrint(order);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, searchParams]);

  const handleViewOrderAndPrint = async (order: any) => {
    setSelectedOrder(order);
    setTrackingNumber(order.tracking_number || '');
    setCarrier(order.carrier || 'USPS');
    try {
      const res = await ordersAPI.getOrder(order.id);
      if (res.success && res.data) {
        const items = res.data.items || [];
        setOrderItems(items);
        if (order.payment_status === 'paid') {
          setPrintData({ order, items });
          setTimeout(() => window.print(), 400);
        }
      }
    } catch {
      toast.error('Failed to load order for printing');
    }
  };

  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      const res = await adminAPI.getAllOrders();
      if (res.success && res.data) setOrders(res.data);
    } catch {
      toast.error('Failed to fetch orders');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (id: string, status: string) => {
    try {
      await ordersAPI.updateOrderStatus(id, status);
      toast.success('Order status updated');
      fetchOrders();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleViewOrder = async (order: any) => {
    setSelectedOrder(order);
    setOrderItems([]);
    setTrackingNumber(order.tracking_number || '');
    setCarrier(order.carrier || 'USPS');
    setIsLoadingItems(true);
    try {
      const res = await ordersAPI.getOrder(order.id);
      if (res.success && res.data) {
        setOrderItems(res.data.items || []);
      }
    } catch {
      toast.error('Failed to load order items');
    } finally {
      setIsLoadingItems(false);
    }
  };

  const handleSaveTracking = async () => {
    if (!selectedOrder) return;
    if (!trackingNumber.trim()) {
      toast.error('Enter a tracking number');
      return;
    }
    try {
      setIsSavingTracking(true);
      await ordersAPI.updateTracking(selectedOrder.id, trackingNumber.trim(), carrier);
      toast.success('Tracking added — customer notified by email');
      setSelectedOrder({ ...selectedOrder, tracking_number: trackingNumber.trim(), carrier, status: 'shipped' });
      fetchOrders();
    } catch {
      toast.error('Failed to save tracking number');
    } finally {
      setIsSavingTracking(false);
    }
  };

  // Print is only available once an order is genuinely paid — printing an
  // unconfirmed order to hand to fulfillment would be misleading.
  const handlePrint = () => {
    if (!selectedOrder || selectedOrder.payment_status !== 'paid') return;
    setPrintData({ order: selectedOrder, items: orderItems });
    setTimeout(() => window.print(), 150);
  };

  const filtered = orders.filter((o) => {
    const matchesSearch =
      o.order_number?.toLowerCase().includes(search.toLowerCase()) ||
      o.customer_email?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter ? o.status === statusFilter : true;
    return matchesSearch && matchesStatus;
  });

  const statusColors: Record<string, string> = {
    pending: 'text-amber-600 bg-amber-50',
    processing: 'text-blue-600 bg-blue-50',
    shipped: 'text-purple-600 bg-purple-50',
    delivered: 'text-green-600 bg-green-50',
    cancelled: 'text-red-500 bg-red-50',
  };

  const statuses = ['pending', 'processing', 'shipped', 'delivered', 'cancelled'];
  const carriers = ['USPS', 'UPS', 'FedEx', 'DHL', 'Other'];

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs tracking-widest uppercase text-nude mb-1">Management</p>
        <h1 className="font-serif text-3xl text-primary">Orders</h1>
      </div>

      {/* Filters */}
      <div className="flex space-x-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-nude" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="SEARCH BY ORDER OR EMAIL"
            className="w-full bg-white border border-light pl-9 pr-4 py-3 text-xs tracking-widest outline-none focus:border-primary transition-colors"
          />
        </div>
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="appearance-none bg-white border border-light px-4 py-3 pr-8 text-xs tracking-widest uppercase outline-none focus:border-primary transition-colors cursor-pointer"
          >
            <option value="">All Status</option>
            {statuses.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
          <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-nude pointer-events-none" />
        </div>
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedOrder(null)}
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            className="bg-white w-full max-w-lg p-8 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-xl text-primary">
                {selectedOrder.order_number}
              </h2>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-nude hover:text-primary transition-colors text-xs tracking-widest uppercase"
              >
                Close
              </button>
            </div>
            <div className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-nude tracking-widest uppercase mb-1">Customer</p>
                  <p>{selectedOrder.customer_name}</p>
                  <p className="text-nude">{selectedOrder.customer_email}</p>
                </div>
                <div>
                  <p className="text-nude tracking-widest uppercase mb-1">Payment</p>
                  <p>{selectedOrder.payment_method || '—'}</p>
                  <p className={selectedOrder.payment_status === 'paid' ? 'text-green-600' : 'text-amber-600'}>
                    {selectedOrder.payment_status}
                  </p>
                </div>
              </div>

              {selectedOrder.shipping_type && selectedOrder.shipping_type !== 'standard' && (
                <div className="bg-amber-50 border border-amber-200 p-4">
                  <p className="text-amber-700 tracking-widest uppercase mb-1 font-medium">
                    {selectedOrder.shipping_type === 'pickup'
                      ? 'Pick Up In Store (Nigeria)'
                      : selectedOrder.shipping_type === 'nigeria_local'
                      ? 'Local Delivery (Nigeria) — Follow Up Required'
                      : 'International Delivery — Follow Up Required'}
                  </p>
                  {selectedOrder.shipping_type !== 'pickup' && (
                    <>
                      <p className="text-secondary leading-relaxed mb-2">
                        Shipping wasn't charged at checkout — contact the customer on WhatsApp with the delivery cost before shipping.
                      </p>
                      <p className="text-secondary">
                        <span className="text-nude">Contact:</span> {selectedOrder.customer_email}
                        {selectedOrder.customer_phone && ` · ${selectedOrder.customer_phone}`}
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Items Ordered */}
              <div className="border-t border-light pt-4">
                <p className="text-nude tracking-widest uppercase mb-3">
                  Items Ordered
                </p>
                {isLoadingItems ? (
                  <div className="space-y-2">
                    <div className="skeleton h-10 w-full rounded" />
                    <div className="skeleton h-10 w-full rounded" />
                  </div>
                ) : orderItems.length === 0 ? (
                  <p className="text-nude">No items found for this order</p>
                ) : (
                  <div className="space-y-3">
                    {orderItems.map((item) => (
                      <div key={item.id} className="border-b border-light pb-3 last:border-b-0">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium tracking-wide">
                              {item.product_name || 'Unknown product'}
                            </p>
                            <p className="text-nude mt-1">
                              {item.size && `Size: ${item.size}`}
                              {item.color && ` / Color: ${item.color}`}
                              {item.sku && ` / SKU: ${item.sku}`}
                            </p>
                            <p className="text-nude">Qty: {item.quantity}</p>
                          </div>
                          <p>${Number(item.price || 0).toFixed(2)}</p>
                        </div>
                        {item.measurements && (
                          <div className="bg-amber-50 border border-amber-200 p-3 mt-2">
                            <p className="text-[10px] tracking-widest uppercase text-amber-700 mb-1.5">
                              Customer Measurements
                            </p>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                              {Object.entries(item.measurements)
                                .filter(([key, val]) => key !== 'comment' && val)
                                .map(([key, val]) => (
                                  <p key={key} className="text-secondary">
                                    <span className="text-nude capitalize">
                                      {key.replace(/_/g, ' ')}:
                                    </span>{' '}
                                    {String(val)}
                                  </p>
                                ))}
                            </div>
                            {item.measurements.comment && (
                              <p className="text-secondary mt-2 pt-2 border-t border-amber-200">
                                <span className="text-nude">Note:</span> {item.measurements.comment}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-light pt-4">
                <div className="flex justify-between mb-2">
                  <span className="text-nude">Subtotal</span>
                  <span>${Number(selectedOrder.subtotal).toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-nude">Shipping</span>
                  <span>${Number(selectedOrder.shipping_cost).toFixed(2)}</span>
                </div>
                {Number(selectedOrder.tax) > 0 && (
                  <div className="flex justify-between mb-2">
                    <span className="text-nude">Tax</span>
                    <span>${Number(selectedOrder.tax).toFixed(2)}</span>
                  </div>
                )}
                {Number(selectedOrder.discount_amount) > 0 && (
                  <div className="flex justify-between mb-2 text-green-600">
                    <span>Discount</span>
                    <span>-${Number(selectedOrder.discount_amount).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-medium pt-2 border-t border-light">
                  <span className="tracking-widest uppercase">Total</span>
                  <span>${Number(selectedOrder.total).toFixed(2)}</span>
                </div>
              </div>

              {/* Shipping / Tracking (Pirate Ship label entered manually) */}
              <div className="border-t border-light pt-4">
                <p className="text-nude tracking-widest uppercase mb-3">
                  Shipping — Pirate Ship
                </p>
                <p className="text-nude mb-3 leading-relaxed">
                  Buy the label for this order in your Pirate Ship dashboard, then paste the tracking number below to notify the customer.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-nude tracking-widest uppercase block mb-1">Carrier</label>
                    <select
                      value={carrier}
                      onChange={(e) => setCarrier(e.target.value)}
                      className="w-full border border-light px-3 py-2 text-xs outline-none focus:border-primary transition-colors"
                    >
                      {carriers.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-nude tracking-widest uppercase block mb-1">Tracking Number</label>
                    <input
                      type="text"
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      placeholder="e.g. 9400100000000000000000"
                      className="w-full border border-light px-3 py-2 text-xs outline-none focus:border-primary transition-colors"
                    />
                  </div>
                </div>
                <button
                  onClick={handleSaveTracking}
                  disabled={isSavingTracking}
                  className="btn-primary w-full text-xs disabled:opacity-50"
                >
                  {isSavingTracking ? 'Saving...' : selectedOrder.tracking_number ? 'Update Tracking' : 'Mark as Shipped & Notify Customer'}
                </button>
                {selectedOrder.tracking_number && (
                  <p className="text-green-600 mt-2">
                    Current tracking: {selectedOrder.tracking_number} ({selectedOrder.carrier})
                  </p>
                )}
              </div>

              {/* Invoice */}
              <div className="border-t border-light pt-4">
                <p className="text-nude tracking-widest uppercase mb-3">
                  Invoice
                </p>
                <button
                  onClick={() => ordersAPI.downloadInvoice(selectedOrder.id, selectedOrder.order_number)}
                  className="btn-secondary w-full text-xs"
                >
                  Download Invoice (PDF)
                </button>
              </div>

              {/* Print — only once payment is confirmed */}
              <div className="border-t border-light pt-4">
                <p className="text-nude tracking-widest uppercase mb-3">
                  Print
                </p>
                {selectedOrder.payment_status === 'paid' ? (
                  <button
                    onClick={handlePrint}
                    className="btn-secondary w-full text-xs"
                  >
                    Print Order Details
                  </button>
                ) : (
                  <p className="text-nude leading-relaxed">
                    Printing is available once this order is paid and confirmed.
                  </p>
                )}
              </div>

              <div className="border-t border-light pt-4">
                <p className="text-nude tracking-widest uppercase mb-3">
                  Update Status
                </p>
                <div className="flex flex-wrap gap-2">
                  {statuses.map((s) => (
                    <button
                      key={s}
                      onClick={() => {
                        handleUpdateStatus(selectedOrder.id, s);
                        setSelectedOrder({ ...selectedOrder, status: s });
                      }}
                      className={`text-xs px-3 py-1 tracking-widest uppercase transition-colors ${
                        selectedOrder.status === s
                          ? 'bg-primary text-white'
                          : 'border border-light hover:border-primary'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Table */}
      <div className="bg-white border border-light overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-light">
              {['Order', 'Customer', 'Status', 'Payment', 'Total', 'Date', 'Actions'].map((h) => (
                <th key={h} className="px-6 py-3 text-left text-xs tracking-widest uppercase text-nude">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center">
                  <div className="skeleton h-6 w-48 mx-auto rounded" />
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-xs text-nude tracking-widest uppercase">
                  No orders found
                </td>
              </tr>
            ) : (
              filtered.map((order) => (
                <tr key={order.id} className="border-b border-light hover:bg-light/50 transition-colors">
                  <td className="px-6 py-4 text-xs font-medium tracking-wide whitespace-nowrap">
                    {order.order_number}
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-xs">{order.customer_name}</p>
                    <p className="text-xs text-nude">{order.customer_email}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs tracking-widest uppercase px-2 py-1 rounded ${
                      statusColors[order.status] || 'text-primary bg-light'
                    }`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs tracking-widest uppercase ${
                      order.payment_status === 'paid' ? 'text-green-600' : 'text-amber-600'
                    }`}>
                      {order.payment_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-xs">
                    ${Number(order.total).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-xs text-nude">
                    {new Date(order.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleViewOrder(order)}
                      className="text-nude hover:text-primary transition-colors"
                    >
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Printable order summary — invisible on screen, only rendered when printing */}
      {printData && (
        <div className="print-only hidden print:block">
          <div style={{ fontFamily: 'Georgia, serif', color: '#0D0D0D' }}>
            <h1 style={{ fontSize: '22px', marginBottom: '4px' }}>LANRE SAVAGE</h1>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '20px' }}>Order Summary — Internal Fulfillment Copy</p>

            <table style={{ width: '100%', marginBottom: '20px', fontSize: '13px' }}>
              <tbody>
                <tr>
                  <td style={{ paddingBottom: '4px' }}><strong>Order Number:</strong></td>
                  <td>{printData.order.order_number}</td>
                  <td style={{ paddingBottom: '4px' }}><strong>Date:</strong></td>
                  <td>{printData.order.created_at ? new Date(printData.order.created_at).toLocaleDateString() : ''}</td>
                </tr>
                <tr>
                  <td><strong>Customer:</strong></td>
                  <td>{printData.order.customer_name}</td>
                  <td><strong>Payment:</strong></td>
                  <td>{printData.order.payment_method} — {printData.order.payment_status}</td>
                </tr>
                <tr>
                  <td><strong>Email:</strong></td>
                  <td colSpan={3}>{printData.order.customer_email}</td>
                </tr>
              </tbody>
            </table>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '20px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #0D0D0D' }}>
                  <th style={{ textAlign: 'left', padding: '6px 0' }}>Item</th>
                  <th style={{ textAlign: 'center', padding: '6px 0' }}>Qty</th>
                  <th style={{ textAlign: 'right', padding: '6px 0' }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {printData.items.map((item) => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '6px 0' }}>
                      {item.product_name || 'Unknown product'}
                      {item.size && ` (${item.size}${item.color ? ' / ' + item.color : ''})`}
                    </td>
                    <td style={{ textAlign: 'center', padding: '6px 0' }}>{item.quantity}</td>
                    <td style={{ textAlign: 'right', padding: '6px 0' }}>${Number(item.price || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {printData.items.some((i) => i.measurements) && (
              <div style={{ marginBottom: '20px', fontSize: '12px' }}>
                <h3 style={{ fontSize: '14px', marginBottom: '8px' }}>Customer Measurements</h3>
                {printData.items.filter((i) => i.measurements).map((i) => (
                  <div key={i.id} style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #eee' }}>
                    <strong>{i.product_name}</strong>
                    <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                      {Object.entries(i.measurements)
                        .filter(([key, val]) => key !== 'comment' && val)
                        .map(([key, val]) => (
                          <li key={key} style={{ textTransform: 'capitalize' }}>
                            {key.replace(/_/g, ' ')}: {String(val)}
                          </li>
                        ))}
                    </ul>
                    {i.measurements.comment && (
                      <p style={{ marginTop: '4px' }}><em>Note: {i.measurements.comment}</em></p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <table style={{ width: '100%', fontSize: '13px', marginTop: '10px' }}>
              <tbody>
                <tr>
                  <td style={{ textAlign: 'right', padding: '2px 0' }}>Subtotal:</td>
                  <td style={{ textAlign: 'right', width: '100px', padding: '2px 0' }}>${Number(printData.order.subtotal || 0).toFixed(2)}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'right', padding: '2px 0' }}>Shipping:</td>
                  <td style={{ textAlign: 'right', padding: '2px 0' }}>${Number(printData.order.shipping_cost || 0).toFixed(2)}</td>
                </tr>
                {Number(printData.order.tax || 0) > 0 && (
                  <tr>
                    <td style={{ textAlign: 'right', padding: '2px 0' }}>Tax:</td>
                    <td style={{ textAlign: 'right', padding: '2px 0' }}>${Number(printData.order.tax || 0).toFixed(2)}</td>
                  </tr>
                )}
                <tr style={{ borderTop: '1px solid #0D0D0D' }}>
                  <td style={{ textAlign: 'right', padding: '6px 0', fontWeight: 'bold' }}>Total:</td>
                  <td style={{ textAlign: 'right', padding: '6px 0', fontWeight: 'bold' }}>${Number(printData.order.total || 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>

            {printData.order.tracking_number && (
              <p style={{ fontSize: '12px', marginTop: '20px' }}>
                <strong>Tracking:</strong> {printData.order.tracking_number} ({printData.order.carrier})
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersAdminPage;